import { GrupoCondicaoSelectModel } from 'components/form/field/select/GrupoCondicaoSelectField'
import { startOfDay, subMonths } from 'date-fns'
import createDecorator from 'final-form-calculate'
import {
  AcompanhamentoCondicaoSaudeTematicaEnum,
  AgeRangeType,
  FaixaEtariaEnum,
  GrupoCondicaoEnum,
  PeriodoCadastroIndividualEnum,
  SexoEnum,
} from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'
import { isObjectDeepEmpty } from 'util/object'
import { isEmpty } from 'util/validation/Util'

import {
  AcompanhamentoCondicaoSaudeFormModel,
  IvcfFilterModel,
  ResultadosTriaEnum,
  TipoVulnerabilidadeEnum,
} from './model/model-acompanhamentoCondicaoSaude'

const meta = metaPath<AcompanhamentoCondicaoSaudeFormModel>()
const metaIvcf = metaPath<IvcfFilterModel>()

export const calculator = createDecorator(
  {
    field: [meta.grupoCondicao.absolutePath(), meta.problemasCondicoes.absolutePath()],
    updates: {
      [meta.ativoListaProblema.absolutePath()]: (
        _: [],
        allValues: AcompanhamentoCondicaoSaudeFormModel,
        prevValues: AcompanhamentoCondicaoSaudeFormModel
      ) => {
        const previousCondicoesWereEmpty = isEmpty(prevValues.grupoCondicao) && isEmpty(prevValues.problemasCondicoes)
        const currentCondicoesAreEmpty = isEmpty(allValues.grupoCondicao) && isEmpty(allValues.problemasCondicoes)

        if (currentCondicoesAreEmpty) return false
        if (isObjectDeepEmpty(prevValues)) return allValues.ativoListaProblema
        if (previousCondicoesWereEmpty && !currentCondicoesAreEmpty) return true

        return allValues.ativoListaProblema
      },
    },
  },
  {
    field: meta.tipoVulnerabilidadeSelected.absolutePath(),
    updates: {
      [meta.tipoFaixaEtaria.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        return allValues.tipoVulnerabilidadeSelected === TipoVulnerabilidadeEnum.IVCF &&
          allValues.tipoFaixaEtaria !== FaixaEtariaEnum.OUTRA
          ? FaixaEtariaEnum.IDOSO
          : allValues.tipoFaixaEtaria
      },
      [meta.ivcf.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        return allValues.tipoVulnerabilidadeSelected === TipoVulnerabilidadeEnum.IVCF ? allValues.ivcf : undefined
      },
      [meta.tria.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        return allValues.tipoVulnerabilidadeSelected === TipoVulnerabilidadeEnum.TRIA ? allValues.tria : undefined
      },
      [meta.tria.periodoRegistro.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.tipoVulnerabilidadeSelected !== TipoVulnerabilidadeEnum.TRIA) return undefined

        return !isEmpty(allValues.tria?.periodoRegistro)
          ? allValues.tria.periodoRegistro
          : PeriodoCadastroIndividualEnum.SEIS_MESES
      },
      [meta.tria.resultadosTria.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.tipoVulnerabilidadeSelected !== TipoVulnerabilidadeEnum.TRIA) return undefined

        return !isEmpty(allValues.tria?.resultadosTria)
          ? allValues.tria.resultadosTria
          : ResultadosTriaEnum.RISCO_DE_INSEGURANCA_ALIMENTAR
      },
      [meta.ivcf.buscaPorPontuacao.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.tipoVulnerabilidadeSelected !== TipoVulnerabilidadeEnum.IVCF) return undefined

        return !isEmpty(allValues.ivcf?.buscaPorPontuacao) ? allValues.ivcf.buscaPorPontuacao : false
      },
      // TODO (Royal Flush): Remover isIvcfListing quando listas temáticas forem desflagueadas
      [meta.isIvcfListing.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        return allValues.tipoVulnerabilidadeSelected === TipoVulnerabilidadeEnum.IVCF ? true : undefined
      },
    },
  },
  // TODO (Royal Flush): Remover quando listas temáticas forem desflagueadas
  {
    field: meta.ivcf.buscaPorPontuacao.absolutePath(),
    updates: {
      [meta.ivcf.riscoVulnerabilidade.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.ivcf === undefined) return undefined
        return allValues.ivcf.buscaPorPontuacao ? undefined : allValues.ivcf.riscoVulnerabilidade
      },
      [meta.ivcf.pontuacao.absolutePath()]: (_: [], allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.ivcf === undefined) return undefined
        return allValues.ivcf.buscaPorPontuacao ? allValues.ivcf.pontuacao : undefined
      },
    },
  },
  {
    field: meta.listaTematica.absolutePath(),
    updates: {
      [meta.problemasCondicoes.absolutePath()]: () => undefined,
      [meta.tipoFaixaEtaria.absolutePath()]: (_, allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA) {
          return FaixaEtariaEnum.IDOSO
        }

        if (
          allValues.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.DESENVOLVIMENTO_INFANTIL ||
          allValues.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.SAUDE_MULHER
        ) {
          return FaixaEtariaEnum.OUTRA
        }

        return FaixaEtariaEnum.TODAS_FAIXAS
      },
      [meta.faixaEtariaRange.absolutePath()]: (_, allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.DESENVOLVIMENTO_INFANTIL) {
          return {
            tipoFaixaEtaria: AgeRangeType.ANOS,
            firstValue: 0,
            secondValue: 2,
          }
        }

        if (allValues.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.SAUDE_MULHER) {
          return {
            tipoFaixaEtaria: AgeRangeType.ANOS,
            firstValue: 14,
            secondValue: 69,
          }
        }
      },
      [meta.sexo.absolutePath()]: (_, allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        if (allValues.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.SAUDE_MULHER) {
          return [SexoEnum.FEMININO, SexoEnum.INDETERMINADO]
        }
      },
      [meta.periodoUltimoAtendimento.absolutePath()]: () => {
        return {
          startDate: subMonths(startOfDay(new Date()), 36),
          endDate: startOfDay(new Date()),
        }
      },
      [meta.ivcf.absolutePath()]: (_, allValues: AcompanhamentoCondicaoSaudeFormModel) => {
        return allValues.listaTematica !== AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA
          ? undefined
          : allValues.ivcf
      },
      [meta.grupoCondicao.absolutePath()]: (value: AcompanhamentoCondicaoSaudeTematicaEnum) => {
        if (value === AcompanhamentoCondicaoSaudeTematicaEnum.DIABETES) {
          return [{ grupo: GrupoCondicaoEnum.DIABETES }] as Partial<GrupoCondicaoSelectModel[]>
        }
        if (value === AcompanhamentoCondicaoSaudeTematicaEnum.HIPERTENSAO_ARTERIAL) {
          return [{ grupo: GrupoCondicaoEnum.HIPERTENSAO_ARTERIAL }] as Partial<GrupoCondicaoSelectModel[]>
        }
      },
      [meta.ativoListaProblema.absolutePath()]: (value: AcompanhamentoCondicaoSaudeTematicaEnum) => {
        if (
          value === AcompanhamentoCondicaoSaudeTematicaEnum.DIABETES ||
          value === AcompanhamentoCondicaoSaudeTematicaEnum.HIPERTENSAO_ARTERIAL
        ) {
          return true
        }
      },
    },
  }
)

export const calculatorFiltrosAvancados = createDecorator({
  field: metaIvcf.buscaPorPontuacao.absolutePath(),
  updates: {
    [metaIvcf.riscoVulnerabilidade.absolutePath()]: (_: [], allValues: IvcfFilterModel) => {
      if (allValues === undefined) return undefined
      return allValues.buscaPorPontuacao ? undefined : allValues.riscoVulnerabilidade
    },
    [metaIvcf.pontuacao.absolutePath()]: (_: [], allValues: IvcfFilterModel) => {
      if (allValues === undefined) return undefined
      return allValues.buscaPorPontuacao ? allValues.pontuacao : undefined
    },
  },
})
